import { DecimalPipe, NgClass } from '@angular/common'
import { Component, OnDestroy, OnInit, signal } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { IonButton, IonIcon, IonMenuToggle, IonSpinner } from '@ionic/angular/standalone'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { addIcons } from 'ionicons'
import {
	analytics,
	bugOutline,
	calendarOutline,
	checkmarkOutline,
	chevronDownOutline,
	chevronForwardOutline,
	closeCircleOutline,
	colorPaletteOutline,
	constructOutline,
	diamondOutline,
	documentTextOutline,
	informationCircleOutline,
	keyOutline,
	languageOutline,
	logOutOutline,
	optionsOutline,
	personCircleOutline,
	personOutline,
	settingsOutline,
	starOutline,
	swapHorizontalOutline,
	text,
	textOutline,
	trendingUpOutline,
	trophyOutline,
	walletOutline,
} from 'ionicons/icons'
import { BehaviorSubject, filter, finalize, map, Subscription, switchMap } from 'rxjs'
import { combineLatestWith, tap } from 'rxjs/operators'

import { environment } from '../../../../environments/environment'
import { VERSION } from '../../../../environments/version'
import { HideForDemoAccountDirective } from '../../../pages/login/directives/hide-for-demo-account.directive'
import { SUPPORTED_LANGUAGES } from '../../constants/common.constant'
import { AuthService } from '../../services/auth.service'
import { BalanceService } from '../../services/balance.service'
import { DisplayService } from '../../services/display.service'
import { DomainService } from '../../services/domain.service'
import { FontSizeService } from '../../services/font-size.service'
import { HelperService } from '../../services/helper.service'
import { ThemeColorService } from '../../services/theme-color.service'
import { WalletTransferService } from '../../services/wallet-transfer.service'
import { WhiteLabelMarketConfigService } from '../../services/white-label-market-config.service'
import { DrawerService } from './drawer.service'
import { Currency } from './types/drawer.type'

@Component({
	selector: 'app-drawer',
	templateUrl: './drawer.component.html',
	styleUrls: ['./drawer.component.scss'],
	standalone: true,
	imports: [DecimalPipe, NgClass, ReactiveFormsModule, TranslatePipe, IonIcon, IonMenuToggle, HideForDemoAccountDirective, IonSpinner, IonButton],
})
export class DrawerComponent implements OnInit, OnDestroy {
	subscriptions$ = new Subscription()

	currency = signal<string>('')

	balance = signal<string | number>(0)

	isUpdatingLanguage = signal<boolean>(false)

	currencies$ = new BehaviorSubject<Currency[]>([])

	languageFormControl = new FormControl<string>('')

	fontSizeFormControl = new FormControl<string>('')

	colorThemeFormControl = new FormControl<string>('')

	displayModeFormControl = new FormControl<string>('')

	showDisplayDropdown = false

	showSettingsDropdown = signal<boolean>(false)

	showFontDropdown = signal<boolean>(false)

	showColorDropdown = signal<boolean>(false)

	showLanguageDropdown = signal<boolean>(false)

	protected readonly SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGES

	protected readonly environment = environment

	get showCurrency() {
		return this.whiteLabelMarketConfigService.showFeature('showCurrency')
	}

	constructor(
		private readonly router: Router,
		protected readonly balanceService: BalanceService,
		private readonly authService: AuthService,
		private readonly drawerService: DrawerService,
		private readonly translateService: TranslateService,
		protected readonly helperService: HelperService,
		protected readonly whiteLabelMarketConfigService: WhiteLabelMarketConfigService,
		protected readonly fontSizeService: FontSizeService,
		protected readonly themeColorService: ThemeColorService,
		protected readonly displayService: DisplayService,
		private readonly domainService: DomainService,
		private readonly walletTransferService: WalletTransferService,
	) {
		this.languageFormControl.patchValue(this.translateService.currentLang)

		this.languageFormControl.valueChanges
			.pipe(
				finalize(() => this.isUpdatingLanguage.set(false)),
				filter((language) => language !== null),
				tap((language) => {
					this.helperService.setSelectedLanguage(language)
					this.isUpdatingLanguage.set(true)
				}),
				map((language) => [language, this.authService.getAccessToken()]),
				filter(([, token]) => token !== null && token !== ''),
				switchMap(([language, token]) => this.drawerService.updateLanguage({ LANGUAGE: language, session_id: token })),
			)
			.subscribe({
				next: (language) => {
					if (language) {
						this.translateService.use(language)
						window.location.reload()
					}
				},
			})

		this.fontSizeFormControl.patchValue(this.fontSizeService.getFontSize())

		this.fontSizeFormControl.valueChanges.subscribe((size) => {
			if (size) this.fontSizeService.setFontSize(size)
		})

		this.colorThemeFormControl.patchValue(this.themeColorService.getThemeColor())

		this.colorThemeFormControl.valueChanges.subscribe((color) => {
			if (color) this.themeColorService.setThemeColor(color)
		})

		this.displayModeFormControl.patchValue(this.displayService.displayMode())
		this.displayModeFormControl.valueChanges.subscribe((mode) => {
			if (mode && (mode === 'time' || mode === 'league')) {
				this.displayService.setMode(mode)
			}
		})

		addIcons({
			personOutline,
			starOutline,
			documentTextOutline,
			trendingUpOutline,
			calendarOutline,
			keyOutline,
			logOutOutline,
			analytics,
			constructOutline,
			text,
			closeCircleOutline,
			trophyOutline,
			diamondOutline,
			languageOutline,
			optionsOutline,
			chevronForwardOutline,
			chevronDownOutline,
			walletOutline,
			settingsOutline,
			swapHorizontalOutline,
			textOutline,
			personCircleOutline,
			informationCircleOutline,
			bugOutline,
			checkmarkOutline,
			colorPaletteOutline,
		})

		this.drawerService.getCurrencyList().subscribe({
			next: (response) => {
				this.currencies$.next(response)
			},
		})
	}

	ngOnInit() {
		this.getBalance()
		this.getCurrencySymbol()
	}

	ngOnDestroy() {
		this.subscriptions$.unsubscribe()
	}

	async logOut() {
		this.authService.logOut()
	}

	async goTo(route: string) {
		await this.router.navigate([route])
	}

	openWalletTransfer() {
		this.router.navigate(['/wallet-transfer'])
	}

	getVersion() {
		return VERSION.tag || VERSION.version
	}

	get showFeedbackButton() {
		return this.environment.SENTRY_ENABLED
	}

	getCurrencySymbol() {
		// Listen to user data changes for currency update after login
		this.subscriptions$.add(
			this.helperService.userDataChanged$
				.pipe(
					filter((userData) => userData !== null),
					map((userData) => userData!.user_fields.CURRENCY),
					combineLatestWith(this.currencies$),
				)
				.subscribe({
					next: ([currencyCode, currencies]) => {
						// Only update if currencyCode is not empty and currencies are available
						if (currencyCode && currencyCode.trim() !== '' && currencies.length > 0) {
							const filteredCurrency = currencies.find((currency) => currency.CODE === currencyCode)
							if (filteredCurrency) {
								this.currency.set(filteredCurrency.SYMBOL.trim())
							}
						}
					},
				}),
		)

		// Check user data immediately for currency (for initial load)
		this.subscriptions$.add(
			this.currencies$.subscribe({
				next: (currencies) => {
					if (currencies.length > 0) {
						const userData = this.helperService.getUserData()
						const currencyCode = userData?.user_fields?.CURRENCY
						// Only update if currencyCode exists and is not empty
						if (currencyCode && currencyCode.trim() !== '') {
							const filteredCurrency = currencies.find((currency) => currency.CODE === currencyCode)
							if (filteredCurrency) {
								this.currency.set(filteredCurrency.SYMBOL.trim())
							}
						}
					}
				},
			}),
		)
	}

	toggleDisplayDropdown() {
		this.showDisplayDropdown = !this.showDisplayDropdown
	}

	selectDisplayMode(mode: string) {
		if (mode === 'time' || mode === 'league') {
			this.displayService.setMode(mode)
		}
	}

	toggleSettingsDropdown() {
		this.showSettingsDropdown.set(!this.showSettingsDropdown())
		if (!this.showSettingsDropdown()) {
			this.showFontDropdown.set(false)
			this.showColorDropdown.set(false)
			this.showLanguageDropdown.set(false)
		}
	}

	toggleFontDropdown() {
		this.showFontDropdown.set(!this.showFontDropdown())
		if (this.showFontDropdown()) {
			this.showLanguageDropdown.set(false)
			this.showColorDropdown.set(false)
		}
	}

	toggleColorDropdown() {
		this.showColorDropdown.set(!this.showColorDropdown())
		if (this.showColorDropdown()) {
			this.showFontDropdown.set(false)
			this.showLanguageDropdown.set(false)
		}
	}

	toggleLanguageDropdown() {
		this.showLanguageDropdown.set(!this.showLanguageDropdown())
		if (this.showLanguageDropdown()) {
			this.showFontDropdown.set(false)
			this.showColorDropdown.set(false)
		}
	}

	protected readonly FONT_SIZE_LABELS: Record<string, string> = {
		xs: 'Extra Small',
		sm: 'Small',
		md: 'Default',
		lg: 'Large',
		xl: 'Extra Large',
		'two-xl': 'Huge',
		'three-xl': 'Titanic',
	}

	getSelectedFontSizeLabel() {
		const key = this.fontSizeFormControl.value ?? 'md'
		return this.FONT_SIZE_LABELS[key] || 'Default'
	}

	getSelectedColorThemeLabel() {
		const key = this.colorThemeFormControl.value ?? 'navy-blue'
		const option = this.themeColorService.colorOptions.find((opt) => opt.key === key)
		return option?.label || 'Navy Blue'
	}

	getSelectedColorThemeColor() {
		const key = this.colorThemeFormControl.value ?? 'navy-blue'
		const option = this.themeColorService.colorOptions.find((opt) => opt.key === key)
		return option?.color || '#152129'
	}

	getSelectedLanguageName() {
		const symbol = this.languageFormControl.value
		return SUPPORTED_LANGUAGES.find((l) => l.symbol === symbol)?.name || 'English'
	}

	getBalance() {
		// Available to bet(ATB) = Balance(B) + (Total Exposure(TE) - Liability(L))
		this.subscriptions$.add(
			this.balanceService.balanceData$.subscribe({
				next: (balanceData) => {
					this.balance.set(balanceData?.BALANCE ?? 0)
				},
			}),
		)
	}
}
